import { auth, db } from "@poseidon/firebase/firebase";
import {
  AuthError,
  createUserWithEmailAndPassword,
  linkWithCredential,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  UserCredential,
} from "firebase/auth";
import { doc, DocumentData, getDoc } from "firebase/firestore";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LinearProgress } from "@mui/material";

type User = UserCredential["user"];

function microsoftProvider() {
  const provider = new OAuthProvider("microsoft.com");
  provider.setCustomParameters({
    prompt: "select_account",
    // Restrict to our Azure AD tenant; without it any Microsoft account can
    // attempt sign-in (the backend still rejects non-provisioned users).
    ...(process.env["NX_MICROSOFT_TENANT_ID"]
      ? { tenant: process.env["NX_MICROSOFT_TENANT_ID"] }
      : {}),
  });
  return provider;
}

interface AuthContextValue {
  user: User | undefined;
  profile: DocumentData | undefined;
  state: string | undefined;
  isAdmin: boolean;
  isDealer: boolean;
  permissions: string[];
  signUp: (email: string, password: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  linkMicrosoftAccount: (
    email: string,
    password: string,
    oauthError: AuthError
  ) => Promise<void>;
  signOut: () => Promise<void>;
  passwordReset: (email: string) => void;
}

const AuthContext = React.createContext<AuthContextValue>(
  {} as AuthContextValue
);

export function useAuth() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
  profileCollection: "admins" | "dealers";
  requiresClaim: string;
}
export default function AuthProvider({
  children,
  profileCollection,
  requiresClaim,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | undefined>(undefined);
  const [profile, setProfile] = useState<DocumentData>();
  const [state, setState] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState<string[]>([]);

  function signUp(email: string, password: string) {
    createUserWithEmailAndPassword(auth, email, password);
  }

  async function signIn(email: string, password: string) {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );

    // Signed in
    await setupAuth(userCredential.user);
  }

  // Admin access is only minted (as session claims, by the backend
  // beforeSignIn trigger) on Microsoft sign-ins, so the admin portal signs in
  // exclusively through this. Password sign-in still works for the same
  // accounts elsewhere — those sessions just never carry admin claims.
  async function signInWithMicrosoft() {
    const userCredential = await signInWithPopup(auth, microsoftProvider());
    await setupAuth(userCredential.user);
  }

  /**
   * One-time flow for existing password accounts: a first Microsoft sign-in
   * throws auth/account-exists-with-different-credential, and the Microsoft
   * credential must be linked from a password session. Signs out afterwards —
   * the caller asks the user to hit "Sign in with Microsoft" again, since
   * only a fresh Microsoft sign-in gets admin session claims.
   */
  async function linkMicrosoftAccount(
    email: string,
    password: string,
    oauthError: AuthError
  ) {
    const pendingCredential = OAuthProvider.credentialFromError(oauthError);
    if (!pendingCredential) {
      throw new Error("No Microsoft credential to link");
    }
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    await linkWithCredential(userCredential.user, pendingCredential);
    await firebaseSignOut(auth);
  }

  function signOut() {
    return firebaseSignOut(auth);
  }

  function passwordReset(email: string) {
    sendPasswordResetEmail(auth, email);
    return;
  }

  // if admin need to set user, otherwise we need to set user to null
  const setupAuth = useCallback(
    async (user: User | null) => {
      if (!user) {
        setUser(undefined);
        setProfile(undefined);
        setState("not-logged-in");
        setPermissions([]);
        return;
      }

      const tokenResult = await user.getIdTokenResult();

      if (tokenResult.claims[requiresClaim]) {
        // get user profile
        const docSnap = await getDoc(doc(db, profileCollection, user.uid));

        if (!docSnap.exists()) {
          console.log("User doesn't exist");
          return false;
        }

        const adminPerms =
          (tokenResult.claims["adminPermissions"] as string[]) || [];
        setPermissions(adminPerms);
        setUser(user);
        setProfile(docSnap.data());

        return setState("logged-in");
      } else {
        return setState("access-denied");
      }
    },
    [profileCollection, requiresClaim]
  );

  useEffect(() => {
    // return unsubscribe function for cleanup when component unmounts
    return onAuthStateChanged(auth, async (user) => {
      await setupAuth(user);
      setLoading(false);
    });
  }, [setupAuth]);

  const value: AuthContextValue = {
    user,
    profile,
    state,
    isAdmin: (user && requiresClaim === "admin") || false,
    isDealer: (user && requiresClaim === "dealer") || false,
    permissions,
    signUp,
    signIn,
    signInWithMicrosoft,
    linkMicrosoftAccount,
    signOut,
    passwordReset,
  };
  return (
    <AuthContext.Provider value={value}>
      {loading ? <LinearProgress /> : children}
    </AuthContext.Provider>
  );
}
